import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Pencil, Trash2 } from 'lucide-react'
import { useUserRole } from '@/hooks/useUserRole'
import { Tooltip } from '@/components/ui/Tooltip'
import { InfoPopover } from '@/components/ui/InfoPopover'
import type { UserTableProps, UserListItem } from '../types'
import {
  USER_MANAGEMENT_POLICIES,
  PROTECTED_ROLES_DISPLAY,
} from '../constants'
import {
  formatUserEmail,
  formatPhoneForDisplay,
  isUserListRowLibertyAllRoles,
  formatDateTime,
  categorizeUserRoles,
} from '../utils/userManagementFormatters'
import { UserTableSortColumnHeader } from './UserTableSortHeader'
import { cn } from '@/lib/utils/helpers'
import styles from '@/features/invitee/styles/invitesParity.module.css'
import '../styles/userTable.css'

function buildUserColWeights(hasSelectionColumn: boolean, canShowActionColumn: boolean): number[] {
  const w: number[] = []
  if (hasSelectionColumn) w.push(3.5)
  w.push(14) // Name
  w.push(19) // Email
  w.push(12) // Contact
  w.push(18) // Role
  w.push(13) // Last Access Date
  w.push(13) // Property
  w.push(9)  // Status
  if (canShowActionColumn) w.push(8) // Action
  return w
}

function colWidthsFromWeights(weights: number[]) {
  const s = weights.reduce((a, b) => a + b, 0)
  return weights.map((n) => `${(n * 100) / s}%`)
}

function RoleBadge({ rolenames }: { rolenames: Array<{ rolename: string }> }) {
  const roleNames = (rolenames ?? [])
    .map((r) => r.rolename?.trim())
    .filter((name): name is string => !!name)

  // Some API rows return `[{ rolename: '' }]` when user has no roles.
  if (roleNames.length === 0) return <span className="text-xs text-slate-400">—</span>

  if (roleNames.length === 1) {
    return (
      <span
        className="inline-block text-xs font-medium text-slate-700 leading-snug break-words max-w-[240px] text-center"
        title={roleNames[0]}
      >
        {roleNames[0]}
      </span>
    )
  }

  const categorized = categorizeUserRoles(roleNames)
  const totalRolesCount = roleNames.length

  return (
    <InfoPopover
      align="start"
      maxWidth={320}
      testId="um-table-roles-multiple-popover"
      className="!bg-[#fffdf4] !border-amber-200/90 !shadow-xl !rounded-2xl !p-3.5 !text-slate-800"
      content={
        <div className="text-xs space-y-2.5">
          {/* Popover Header */}
          <div className="flex items-center justify-between pb-2 border-b border-amber-200/50">
            <div className="flex items-center gap-1.5 font-bold text-slate-800 text-xs">
              <User className="h-3.5 w-3.5 text-[rgb(21_151_255)]" />
              <span>Assigned roles</span>
            </div>
          </div>

          {/* Categorized Role Groups */}
          <div className="space-y-3 py-0.5">
            {categorized.map((group) => (
              <div key={group.category} className="space-y-1.5">
                <div className="font-bold text-slate-800 text-[11px]">
                  {group.category}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {group.roles.map((name) => (
                    <span
                      key={name}
                      className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-medium bg-[#e0f2fe] text-[#0369a1] border border-[#bae6fd]/60 leading-tight"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="pt-2 border-t border-amber-200/60 text-[11px] font-medium text-slate-500">
            {totalRolesCount} {totalRolesCount === 1 ? 'role assigned' : 'roles assigned'}
          </div>
        </div>
      }
    >
      <button
        type="button"
        data-testid="um-table-roles-multiple"
        className="cursor-pointer select-none text-xs font-medium text-[rgb(21_151_255)] underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-200"
      >
        Multiple Roles…
      </button>
    </InfoPopover>
  )
}

function StatusBadge({ status }: { status: string | null }) {
  const s = status ?? 'Active'
  const colorMap: Record<string, string> = {
    Active: 'bg-emerald-50 text-emerald-700',
    Inactive: 'bg-rose-50 text-rose-700',
    Locked: 'bg-amber-50 text-amber-700',
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        colorMap[s] ?? 'bg-slate-50 text-slate-700'
      )}
    >
      {s}
    </span>
  )
}

export function UserTable({
  users,
  onSort,
  sortBy,
  orderBy,
  sorting,
  selectedIds,
  onSelectOne,
  onSelectAll,
  onImpersonate,
  onDelete,
}: UserTableProps) {
  const navigate = useNavigate()
  const { isAdmin, hasPolicy } = useUserRole()

  const canSelectAll = hasPolicy([USER_MANAGEMENT_POLICIES.SELECT_ALL])
  const canSelectUser = hasPolicy([USER_MANAGEMENT_POLICIES.SELECT_USER])
  const canEdit = hasPolicy([USER_MANAGEMENT_POLICIES.EDIT_USER])
  const canDelete = hasPolicy([USER_MANAGEMENT_POLICIES.DELETE_USER])
  const canImpersonateApplicant = hasPolicy([USER_MANAGEMENT_POLICIES.IMPERSONATE_APPLICANT])
  const canImpersonatePmc = hasPolicy([USER_MANAGEMENT_POLICIES.IMPERSONATE_PMC])

  const hasSelectionColumn = canSelectAll || canSelectUser
  const canShowActionColumn = canEdit || canDelete || canImpersonateApplicant || canImpersonatePmc

  const colWeights = useMemo(
    () => buildUserColWeights(hasSelectionColumn, canShowActionColumn),
    [hasSelectionColumn, canShowActionColumn]
  )
  const colWidths = useMemo(() => colWidthsFromWeights(colWeights), [colWeights])

  const colCount = colWeights.length

  const isProtectedRole = (user: UserListItem): boolean =>
    user.rolename?.some((r) => (PROTECTED_ROLES_DISPLAY as readonly string[]).includes(r.rolename))

  const canImpersonateUser = (user: UserListItem): boolean => {
    const roles = user.rolename ?? []
    if (roles.length !== 1 || !user.role_id || isUserListRowLibertyAllRoles(user)) return false
    const rn = roles[0]?.rolename ?? ''
    if (rn === 'Applicant' || rn === 'Resident' || rn === 'Past Resident') {
      return canImpersonateApplicant
    }
    return canImpersonatePmc
  }

  const canDeleteUser = (user: UserListItem): boolean => {
    if (!canDelete) return false
    if (isProtectedRole(user)) return isAdmin
    return true
  }

  const canSelectUserRow = (user: UserListItem): boolean => {
    if (!canSelectUser) return false
    if (isProtectedRole(user)) return isAdmin
    return true
  }

  return (
    <div data-testid="um-list-table-card" className={styles.tableCard}>
      <div
        data-testid="um-list-table-scroll"
        className={cn(styles.tableResponsiveScroll, 'touch-scroll relative overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm')}
      >
        <table
          data-testid="um-table"
          className={cn(
            styles.listingTable,
            styles.propertyListingTable,
            'table-fixed w-full min-w-[85rem] !max-w-none [&_thead_th]:!whitespace-nowrap [&_tbody_td:not([colspan])]:!whitespace-normal',
            '[&_tbody_td:not([colspan])]:break-words',
            '[&_thead_th]:!px-2.5 [&_thead_th]:!py-2 [&_thead_th]:!align-middle',
            '[&_tbody_td]:!px-2.5 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[13px] [&_tbody_td]:!leading-snug',
          )}
        >
          <colgroup>
            {colWidths.map((width, i) => (
              <col key={i} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr className={cn(sorting && 'pointer-events-none opacity-80')}>
              {hasSelectionColumn && (
                <th className="!text-center !align-middle">
                  {canSelectAll && (
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                      checked={users.length > 0 && users.filter((u) => canSelectUserRow(u)).every((u) => selectedIds.includes(u.user_id))}
                      onChange={(e) => onSelectAll(e.target.checked)}
                      aria-label="Select all users"
                      data-testid="um-table-select-all-checkbox"
                    />
                  )}
                </th>
              )}
              <UserTableSortColumnHeader field="name" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={sorting}>
                Name
              </UserTableSortColumnHeader>
              <UserTableSortColumnHeader field="email" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={sorting}>
                Email
              </UserTableSortColumnHeader>
              <UserTableSortColumnHeader field="mobile_no" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={sorting}>
                Contact
              </UserTableSortColumnHeader>
              <th className="!text-center !align-middle">Role</th>
              <UserTableSortColumnHeader field="last_active" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={sorting}>
                Last Access Date
              </UserTableSortColumnHeader>
              <UserTableSortColumnHeader field="property_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={sorting}>
                Property
              </UserTableSortColumnHeader>
              <UserTableSortColumnHeader align="center" field="account_status" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={sorting}>
                Status
              </UserTableSortColumnHeader>
              {canShowActionColumn && (
                <th
                  className="sticky right-0 z-[2] bg-[#e5eef7] !text-center !align-middle border-l border-slate-200 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)] min-w-[125px]"
                  data-testid="um-list-table-header-actions"
                >
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody data-testid="um-table-body">
            {users.length === 0 ? (
              <tr data-testid="um-table-empty-row">
                <td colSpan={colCount} className="!py-12 text-center text-slate-500">
                  No user found
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const isSelected = selectedIds.includes(user.user_id)
                return (
                  <tr
                    key={user.user_id}
                    className={cn(
                      'group transition-colors',
                      isSelected
                        ? 'bg-[#e0f2fe] hover:bg-[#bae6fd]'
                        : 'even:bg-[#f2f7fd] hover:bg-slate-100/70'
                    )}
                    data-testid={`um-table-row-${user.user_id}`}
                  >
                    {hasSelectionColumn && (
                      <td className="!text-center">
                        {canSelectUserRow(user) && (
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                            checked={isSelected}
                            onChange={(e) => onSelectOne(user.user_id, e.target.checked)}
                            aria-label={`Select ${user.name}`}
                            data-testid={`um-table-select-checkbox-${user.user_id}`}
                          />
                        )}
                      </td>
                    )}
                    <td>
                      <span className="font-medium text-slate-900" data-testid={`um-table-name-${user.user_id}`}>
                        {user.name}
                      </span>
                    </td>
                    <td>
                      <span className="text-slate-600 break-all" data-testid={`um-table-email-${user.user_id}`}>
                        {formatUserEmail(user.email)}
                      </span>
                    </td>
                    <td>
                      <span className="text-slate-600" data-testid={`um-table-contact-${user.user_id}`}>
                        {formatPhoneForDisplay(user.mobile_no)}
                      </span>
                    </td>
                    <td className="!text-center" data-testid={`um-table-roles-${user.user_id}`}>
                      <div className="flex justify-center">
                        <RoleBadge rolenames={user.rolename ?? []} />
                      </div>
                    </td>
                    <td className="whitespace-nowrap text-slate-600">
                      <span data-testid={`um-table-last-active-${user.user_id}`}>{formatDateTime(user.last_active) || '—'}</span>
                    </td>
                    <td className="text-slate-600">
                      <span data-testid={`um-table-property-${user.user_id}`}>{user.property_name ?? '—'}</span>
                    </td>
                    <td className="!text-center" data-testid={`um-table-status-${user.user_id}`}>
                      <StatusBadge status={user.account_status} />
                    </td>
                    {canShowActionColumn && (
                      <td
                        className={cn(
                          'sticky right-0 z-[1] !text-center whitespace-nowrap border-l border-slate-200 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)] px-3 transition-colors',
                          isSelected
                            ? 'bg-[#e0f2fe] group-hover:bg-[#bae6fd]'
                            : 'bg-white group-even:bg-[#f2f7fd] group-hover:bg-[#f1f5f9]'
                        )}
                      >
                      <div className="flex items-center justify-center gap-1">
                        {/* Fixed slot for Impersonate */}
                        {(canImpersonateApplicant || canImpersonatePmc) && (
                          <div className="flex h-7 w-7 items-center justify-center shrink-0">
                            {canImpersonateUser(user) ? (
                              <Tooltip content="Impersonate">
                                <button
                                  type="button"
                                  onClick={() => onImpersonate(user.role_id)}
                                  className="rounded-lg p-1.5 text-slate-500 transition-all hover:bg-[rgb(21_151_255)]/10 hover:text-[rgb(21_151_255)]"
                                  aria-label={`Impersonate ${user.name}`}
                                  data-testid={`um-table-impersonate-btn-${user.user_id}`}
                                >
                                  <User className="h-4 w-4" />
                                </button>
                              </Tooltip>
                            ) : (
                              <span className="h-7 w-7" aria-hidden />
                            )}
                          </div>
                        )}
                        {/* Fixed slot for Edit */}
                        {canEdit && (
                          <div className="flex h-7 w-7 items-center justify-center shrink-0">
                            <Tooltip content="Edit">
                              <button
                                type="button"
                                onClick={() => navigate(`/user-management/edit/${user.user_id}`)}
                                className="rounded-lg p-1.5 text-slate-500 transition-all hover:bg-emerald-50 hover:text-emerald-600"
                                aria-label={`Edit ${user.name}`}
                                data-testid={`um-table-edit-btn-${user.user_id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            </Tooltip>
                          </div>
                        )}
                        {/* Fixed slot for Delete */}
                        {canDelete && (
                          <div className="flex h-7 w-7 items-center justify-center shrink-0">
                            {canDeleteUser(user) ? (
                              <Tooltip content="Delete">
                                <button
                                  type="button"
                                  onClick={() => onDelete(user)}
                                  className="rounded-lg p-1.5 text-slate-500 transition-all hover:bg-rose-50 hover:text-rose-600"
                                  aria-label={`Delete ${user.name}`}
                                  data-testid={`um-table-delete-btn-${user.user_id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </Tooltip>
                            ) : (
                              <span className="h-7 w-7" aria-hidden />
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              )
            })
          )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
